import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useTranslation } from '../i18n/useTranslation';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const MAX_PDF_MB = 8;
const ACCENT_OPTIONS = ['blue', 'indigo', 'rose', 'emerald'];

const emptyItem = () => ({ name: { en: '', ar: '' }, price: '', discountPrice: '' });
const emptyCategory = () => ({ title: { en: '', ar: '' }, accent: 'blue', items: [emptyItem()] });

const authHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const AdminPricing = ({ showToast }) => {
  const { t, language } = useTranslation();
  const isRTL = language === 'ar';
  const fileInputRef = useRef(null);

  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [openSection, setOpenSection] = useState('general');

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/admin/pricing`, authHeaders());
      setForm(res.data);
    } catch (error) {
      console.error('Error loading pricing:', error);
      showToast(t('admin.pricingLoadError'), 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await axios.put(`${API_URL}/admin/pricing`, {
        enabled: form.enabled,
        heading: form.heading,
        subheading: form.subheading,
        currency: form.currency,
        categories: form.categories,
        notes: form.notes,
        promo: form.promo
      }, authHeaders());
      setForm(res.data);
      showToast(t('admin.pricingSaved'), 'success');
    } catch (error) {
      console.error('Error saving pricing:', error);
      showToast(t('admin.pricingSaveError'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const resetToDefault = async () => {
    if (!window.confirm(t('admin.pricingResetConfirm'))) return;
    try {
      const res = await axios.post(`${API_URL}/admin/pricing/reset`, {}, authHeaders());
      setForm(res.data);
      showToast(t('admin.pricingSaved'), 'success');
    } catch (error) {
      showToast(t('admin.pricingSaveError'), 'error');
    }
  };

  const handlePdf = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!/pdf$/i.test(file.type)) {
      showToast(t('admin.pdfTypeError'), 'error');
      event.target.value = '';
      return;
    }
    if (file.size > MAX_PDF_MB * 1024 * 1024) {
      showToast(t('admin.pdfSizeError'), 'error');
      event.target.value = '';
      return;
    }

    setUploading(true);
    try {
      const data = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result).split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const res = await axios.put(`${API_URL}/admin/pricing/pdf`, {
        fileName: file.name,
        mimeType: file.type || 'application/pdf',
        data
      }, authHeaders());

      setForm(res.data);
      showToast(t('admin.pdfUploaded'), 'success');
    } catch (error) {
      console.error('Error uploading PDF:', error);
      showToast(error.response?.data?.message || t('admin.pdfUploadError'), 'error');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const deletePdf = async () => {
    if (!window.confirm(t('admin.pdfDeleteConfirm'))) return;
    try {
      const res = await axios.delete(`${API_URL}/admin/pricing/pdf`, authHeaders());
      setForm(res.data);
      showToast(t('admin.pdfDeleted'), 'success');
    } catch (error) {
      showToast(t('admin.pdfUploadError'), 'error');
    }
  };

  // ----- category / item helpers -----
  const updateCategory = (ci, patch) => setForm(prev => ({
    ...prev,
    categories: prev.categories.map((c, i) => (i === ci ? { ...c, ...patch } : c))
  }));

  const updateItem = (ci, ii, patch) => setForm(prev => ({
    ...prev,
    categories: prev.categories.map((c, i) => i === ci
      ? { ...c, items: c.items.map((it, j) => (j === ii ? { ...it, ...patch } : it)) }
      : c)
  }));

  const addCategory = () => setForm(prev => ({
    ...prev,
    categories: [...prev.categories, emptyCategory()]
  }));

  const removeCategory = (ci) => setForm(prev => ({
    ...prev,
    categories: prev.categories.filter((_, i) => i !== ci)
  }));

  const addItem = (ci) => setForm(prev => ({
    ...prev,
    categories: prev.categories.map((c, i) => i === ci ? { ...c, items: [...c.items, emptyItem()] } : c)
  }));

  const removeItem = (ci, ii) => setForm(prev => ({
    ...prev,
    categories: prev.categories.map((c, i) => i === ci
      ? { ...c, items: c.items.filter((_, j) => j !== ii) }
      : c)
  }));

  const updatePromoItem = (i, patch) => setForm(prev => ({
    ...prev,
    promo: { ...prev.promo, items: prev.promo.items.map((it, j) => (j === i ? { ...it, ...patch } : it)) }
  }));

  const addPromoItem = () => setForm(prev => ({
    ...prev,
    promo: { ...prev.promo, items: [...(prev.promo?.items || []), emptyItem()] }
  }));

  const removePromoItem = (i) => setForm(prev => ({
    ...prev,
    promo: { ...prev.promo, items: prev.promo.items.filter((_, j) => j !== i) }
  }));

  if (loading || !form) {
    return (
      <div className="bg-white rounded-lg shadow-md p-10 flex justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
      </div>
    );
  }

  const Section = ({ id, title, description, children }) => (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      <button
        type="button"
        onClick={() => setOpenSection(openSection === id ? null : id)}
        aria-expanded={openSection === id}
        className="w-full flex items-center justify-between gap-3 p-4 sm:p-6 text-start hover:bg-gray-50 transition"
      >
        <span className="min-w-0">
          <span className="block text-base sm:text-lg font-semibold text-gray-900">{title}</span>
          {description && <span className="block text-xs sm:text-sm text-gray-600 mt-0.5">{description}</span>}
        </span>
        <svg
          className={`w-5 h-5 flex-shrink-0 text-gray-400 transition-transform ${openSection === id ? 'rotate-180' : ''}`}
          fill="none" stroke="currentColor" viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {openSection === id && (
        <div className="px-4 sm:px-6 pb-5 sm:pb-6 pt-4 space-y-4 border-t border-gray-100">
          {children}
        </div>
      )}
    </div>
  );

  const bilingualInput = (labelText, value, onChange) => (
    <div>
      <label className="block text-xs font-semibold text-gray-700 mb-1">{labelText}</label>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <input
          type="text"
          value={value?.en || ''}
          onChange={(e) => onChange({ ...value, en: e.target.value })}
          placeholder="English"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
          dir="ltr"
        />
        <input
          type="text"
          value={value?.ar || ''}
          onChange={(e) => onChange({ ...value, ar: e.target.value })}
          placeholder="العربية"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
          dir="rtl"
        />
      </div>
    </div>
  );

  return (
    <form onSubmit={save} className="space-y-4" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900">{t('admin.pricing')}</h2>
            <p className="text-sm text-gray-600 mt-1">{t('admin.pricingDescription')}</p>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.enabled !== false}
              onChange={(e) => setForm(prev => ({ ...prev, enabled: e.target.checked }))}
              className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
            />
            <span className="text-sm font-medium text-gray-700">{t('admin.pricingEnabled')}</span>
          </label>
        </div>
      </div>

      {/* PDF */}
      <Section id="pdf" title={t('admin.pricingPdf')} description={t('admin.pricingPdfDescription')}>
        {form.pdf?.available ? (
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center gap-3 min-w-0">
              <svg className="w-8 h-8 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate">{form.pdf.fileName}</p>
                <p className="text-xs text-gray-600">{(form.pdf.size / 1024).toFixed(0)} KB</p>
              </div>
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <a
                href={`${API_URL}/pricing/pdf`}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium"
              >
                {t('admin.pdfView')}
              </a>
              <button
                type="button"
                onClick={deletePdf}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium"
              >
                {t('delete')}
              </button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-500 p-4 bg-gray-50 border border-dashed border-gray-300 rounded-lg text-center">
            {t('admin.pdfNone')}
          </p>
        )}

        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf,.pdf"
            onChange={handlePdf}
            className="hidden"
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium text-sm"
          >
            {uploading ? (
              <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>
            )}
            {form.pdf?.available ? t('admin.pdfReplace') : t('admin.pdfUpload')}
          </button>
          <p className="text-xs text-gray-500 mt-2">{t('admin.pdfHint')}</p>
        </div>
      </Section>

      {/* Page header text */}
      <Section id="general" title={t('admin.pricingHeader')} description={t('admin.pricingHeaderDescription')}>
        {bilingualInput(t('admin.pricingHeading'), form.heading, v => setForm(p => ({ ...p, heading: v })))}
        {bilingualInput(t('admin.pricingSubheading'), form.subheading, v => setForm(p => ({ ...p, subheading: v })))}
        {bilingualInput(t('admin.pricingCurrency'), form.currency, v => setForm(p => ({ ...p, currency: v })))}
      </Section>

      {/* Categories */}
      <Section id="categories" title={t('admin.pricingCategories')} description={t('admin.pricingCategoriesDescription')}>
        {form.categories?.map((category, ci) => (
          <div key={ci} className="border-2 border-gray-200 rounded-lg p-3 sm:p-4 space-y-3">
            <div className="flex items-start justify-between gap-2">
              <span className="text-xs font-bold text-gray-400 uppercase tracking-wide pt-1">
                #{ci + 1}
              </span>
              <button
                type="button"
                onClick={() => removeCategory(ci)}
                className="text-red-600 hover:text-red-700 text-xs font-semibold px-2 py-1 hover:bg-red-50 rounded"
              >
                {t('admin.removeCategory')}
              </button>
            </div>

            {bilingualInput(t('admin.categoryTitle'), category.title, v => updateCategory(ci, { title: v }))}

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">{t('admin.categoryColor')}</label>
              <div className="flex gap-2 flex-wrap">
                {ACCENT_OPTIONS.map(accent => (
                  <button
                    key={accent}
                    type="button"
                    onClick={() => updateCategory(ci, { accent })}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border-2 capitalize transition ${
                      category.accent === accent
                        ? 'border-gray-900 bg-gray-900 text-white'
                        : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    {accent}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2 pt-2 border-t border-gray-100">
              {category.items?.map((item, ii) => (
                <div key={ii} className="bg-gray-50 rounded-lg p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-gray-500">{ii + 1}</span>
                    <button
                      type="button"
                      onClick={() => removeItem(ci, ii)}
                      className="text-red-600 hover:text-red-700 text-xs font-medium"
                    >
                      {t('delete')}
                    </button>
                  </div>
                  {bilingualInput(t('admin.serviceName'), item.name, v => updateItem(ci, ii, { name: v }))}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">{t('admin.price')}</label>
                      <input
                        type="text"
                        value={item.price || ''}
                        onChange={(e) => updateItem(ci, ii, { price: e.target.value })}
                        placeholder="300-500"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                        dir="ltr"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">{t('admin.discountPrice')}</label>
                      <input
                        type="text"
                        value={item.discountPrice || ''}
                        onChange={(e) => updateItem(ci, ii, { discountPrice: e.target.value })}
                        placeholder="200"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                        dir="ltr"
                      />
                    </div>
                  </div>
                </div>
              ))}
              <button
                type="button"
                onClick={() => addItem(ci)}
                className="w-full py-2 border-2 border-dashed border-gray-300 text-gray-600 rounded-lg hover:border-blue-400 hover:text-blue-600 text-sm font-medium transition"
              >
                + {t('admin.addService')}
              </button>
            </div>
          </div>
        ))}

        <button
          type="button"
          onClick={addCategory}
          className="w-full py-3 border-2 border-dashed border-blue-300 text-blue-600 rounded-lg hover:bg-blue-50 font-medium text-sm transition"
        >
          + {t('admin.addCategory')}
        </button>
      </Section>

      {/* Promo */}
      <Section id="promo" title={t('admin.promoSection')} description={t('admin.promoDescription')}>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={Boolean(form.promo?.enabled)}
            onChange={(e) => setForm(p => ({ ...p, promo: { ...p.promo, enabled: e.target.checked } }))}
            className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
          />
          <span className="text-sm font-medium text-gray-700">{t('admin.promoEnabled')}</span>
        </label>

        {bilingualInput(t('admin.promoTitle'), form.promo?.title, v => setForm(p => ({ ...p, promo: { ...p.promo, title: v } })))}
        {bilingualInput(t('admin.promoSubtitle'), form.promo?.subtitle, v => setForm(p => ({ ...p, promo: { ...p.promo, subtitle: v } })))}

        <div className="space-y-2 pt-2 border-t border-gray-100">
          {form.promo?.items?.map((item, i) => (
            <div key={i} className="bg-gray-50 rounded-lg p-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-gray-500">{i + 1}</span>
                <button
                  type="button"
                  onClick={() => removePromoItem(i)}
                  className="text-red-600 hover:text-red-700 text-xs font-medium"
                >
                  {t('delete')}
                </button>
              </div>
              {bilingualInput(t('admin.packageName'), item.name, v => updatePromoItem(i, { name: v }))}
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">{t('admin.price')}</label>
                <input
                  type="text"
                  value={item.price || ''}
                  onChange={(e) => updatePromoItem(i, { price: e.target.value })}
                  placeholder="1800"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                  dir="ltr"
                />
              </div>
            </div>
          ))}
          <button
            type="button"
            onClick={addPromoItem}
            className="w-full py-2 border-2 border-dashed border-gray-300 text-gray-600 rounded-lg hover:border-blue-400 hover:text-blue-600 text-sm font-medium transition"
          >
            + {t('admin.addPackage')}
          </button>
        </div>
      </Section>

      {/* Notes */}
      <Section id="notes" title={t('admin.pricingNotes')} description={t('admin.pricingNotesDescription')}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">English</label>
            <textarea
              value={(form.notes?.en || []).join('\n')}
              onChange={(e) => setForm(p => ({ ...p, notes: { ...p.notes, en: e.target.value.split('\n').filter(Boolean) } }))}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              dir="ltr"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">العربية</label>
            <textarea
              value={(form.notes?.ar || []).join('\n')}
              onChange={(e) => setForm(p => ({ ...p, notes: { ...p.notes, ar: e.target.value.split('\n').filter(Boolean) } }))}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              dir="rtl"
            />
          </div>
        </div>
        <p className="text-xs text-gray-500">{t('admin.notesHint')}</p>
      </Section>

      {/* Sticky save bar */}
      <div className="sticky bottom-0 bg-white border border-gray-200 rounded-lg shadow-lg p-3 sm:p-4">
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
          <button
            type="submit"
            disabled={saving}
            className="w-full sm:w-auto px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium"
          >
            {saving ? t('admin.saving') : t('admin.saveFooterHours')}
          </button>
          <button
            type="button"
            onClick={resetToDefault}
            className="w-full sm:w-auto px-5 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
          >
            {t('admin.resetToDefault')}
          </button>
          <a
            href="/prices"
            target="_blank"
            rel="noopener noreferrer"
            className="w-full sm:w-auto px-5 py-2.5 border border-blue-300 text-blue-700 rounded-lg hover:bg-blue-50 font-medium text-center"
          >
            {t('admin.previewPage')}
          </a>
        </div>
      </div>
    </form>
  );
};

export default AdminPricing;
